import {
  FilterAutocompleteChip,
  FilterAutocompleteChipProps,
  FilterDateTimeRangeChip,
  FilterDateTimeRangeChipProps,
  FilterDateTimeRangeTimezoneChip,
  FilterDateTimeRangeTimezoneChipProps,
  FilterEnumAutocompleteChip,
  FilterEnumAutocompleteChipProps,
  FilterEnumMultiselectChip,
  FilterEnumMultiselectChipProps,
  FilterFromToChip,
  FilterFromToChipProps,
  FilterIdChip,
  FilterIdChipProps,
  FilterMultiselectChip,
  FilterMultiselectChipProps,
  FilterSelectChoicesChip,
  FilterSelectChoicesChipProps,
  FilterTextChip,
  FilterTextChipProps,
} from 'shared/mui/Filter/FilterChipsToolbar/FilterChips';

type FilterChipBaseProps = {
  name: string;
  label: string;
};

type FilterChipPropsMap = {
  text: Omit<FilterTextChipProps, keyof FilterChipBaseProps>;
  id: Omit<FilterIdChipProps, keyof FilterChipBaseProps>;
  selectChoices: Omit<FilterSelectChoicesChipProps, keyof FilterChipBaseProps>;
  fromTo: Omit<FilterFromToChipProps, keyof FilterChipBaseProps>;
  dateTimeRange: Omit<FilterDateTimeRangeChipProps, keyof FilterChipBaseProps>;
  dateTimeRangeTimezone: Omit<FilterDateTimeRangeTimezoneChipProps, keyof FilterChipBaseProps>;
  multiselect: Omit<FilterMultiselectChipProps, keyof FilterChipBaseProps>;
  autocomplete: Omit<FilterAutocompleteChipProps, keyof FilterChipBaseProps>;
  enumMultiselect: Omit<FilterEnumMultiselectChipProps, keyof FilterChipBaseProps>;
  enumAutocomplete: Omit<FilterEnumAutocompleteChipProps, keyof FilterChipBaseProps>;
};

type FilterChipType = keyof FilterChipPropsMap;

type ChipInput<T> = T extends { children?: infer Input } ? Input : React.ReactNode;

type ChipProps<K extends FilterChipType> = Omit<FilterChipPropsMap[K], 'children'>;

type RequiredKeys<T> = {
  [K in keyof T]-?: Pick<T, K> extends Required<Pick<T, K>> ? K : never;
}[keyof T];

type ChipPropsField<K extends FilterChipType> =
  RequiredKeys<ChipProps<K>> extends never
    ? { chipProps?: ChipProps<K> }
    : { chipProps: ChipProps<K> };

type FilterInputVariant = 'field' | 'chip';

type FilterInput<T> = T | ((variant: FilterInputVariant) => T);

export type FilterFieldConfig = {
  [K in FilterChipType]: FilterChipBaseProps & {
    chipType: K;
    input?: FilterInput<ChipInput<FilterChipPropsMap[K]>>;
  } & ChipPropsField<K>;
}[FilterChipType];

export const renderFilterChip = (field: FilterFieldConfig) => {
  const { name, label } = field;

  switch (field.chipType) {
    case 'text':
      return (
        <FilterTextChip key={name} label={label} name={name} {...field.chipProps}>
          {resolveFilterInput(field.input, 'chip')}
        </FilterTextChip>
      );
    case 'id':
      return <FilterIdChip key={name} label={label} name={name} {...field.chipProps} />;
    case 'selectChoices':
      return (
        <FilterSelectChoicesChip key={name} label={label} name={name} {...field.chipProps}>
          {resolveFilterInput(field.input, 'chip')}
        </FilterSelectChoicesChip>
      );
    case 'fromTo':
      return <FilterFromToChip key={name} label={label} {...field.chipProps} />;
    case 'dateTimeRange':
      return (
        <FilterDateTimeRangeChip key={name} label={label} name={name} {...field.chipProps}>
          {resolveFilterInput(field.input, 'chip')}
        </FilterDateTimeRangeChip>
      );
    case 'dateTimeRangeTimezone':
      return (
        <FilterDateTimeRangeTimezoneChip key={name} label={label} name={name} {...field.chipProps}>
          {resolveFilterInput(field.input, 'chip')}
        </FilterDateTimeRangeTimezoneChip>
      );
    case 'multiselect':
      return (
        <FilterMultiselectChip key={name} label={label} name={name} {...field.chipProps}>
          {resolveFilterInput(field.input, 'chip')}
        </FilterMultiselectChip>
      );
    case 'autocomplete':
      return (
        <FilterAutocompleteChip key={name} label={label} name={name} {...field.chipProps}>
          {resolveFilterInput(field.input, 'chip')}
        </FilterAutocompleteChip>
      );
    case 'enumMultiselect':
      return (
        <FilterEnumMultiselectChip key={name} label={label} name={name} {...field.chipProps}>
          {resolveFilterInput(field.input, 'chip')}
        </FilterEnumMultiselectChip>
      );
    case 'enumAutocomplete':
      return (
        <FilterEnumAutocompleteChip key={name} label={label} name={name} {...field.chipProps}>
          {resolveFilterInput(field.input, 'chip')}
        </FilterEnumAutocompleteChip>
      );
  }
};

export const resolveFilterInput = <T,>(
  input: FilterInput<T> | undefined,
  variant: FilterInputVariant,
): T | undefined =>
  typeof input === 'function' ? (input as (variant: FilterInputVariant) => T)(variant) : input;
